import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import { spacing } from "../../../design-system/tokens/spacing";
import { shadows } from "../../../design-system/tokens/shadows";
import {
  borderRadius,
  borderStyle,
  borderWidth,
} from "../../../design-system/tokens/borders";

/** Reserves the collapsed width so an expanded rail overlays the page instead of pushing it. */
export const RailAnchor = styled.div`
  position: relative;
  width: ${spacing[16]};
  min-width: ${spacing[16]};
  height: 100%;
  flex: 1 1 auto;
  align-self: stretch;
`;

export const ExpandToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[10]};
  height: ${spacing[10]};
  padding: 0;
  border: 0;
  border-radius: ${borderRadius.lg};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.text.interactive};
    background: ${({ theme }) => theme.colors.primary[50]};
  }
  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid} ${({ theme }) => theme.colors.primary[500]};
    outline-offset: -${borderWidth[2]};
  }
`;

export const ItemLabel = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const Rail = styled.nav<{ $expandable?: boolean; $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: ${spacing[16]};
  min-width: ${spacing[16]};
  max-width: ${spacing[16]};
  height: 100%;
  min-height: 0;
  flex: 1 1 auto;
  align-self: stretch;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.background.primary};
  border: ${borderWidth[1]} ${borderStyle.solid}
    ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${borderRadius.xl};
  ${({ $expandable, $expanded, theme }) =>
    $expandable &&
    css`
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      z-index: 30;
      overflow: hidden;
      width: ${$expanded ? spacing[56] : spacing[16]};
      max-width: ${$expanded ? spacing[56] : spacing[16]};
      transition: width ${theme.transitions.durations.normal} ${theme.transitions.easing.easeInOut};
      box-shadow: ${$expanded ? shadows.elevation[4] : "none"};
    `}
`;

export const NavigationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding: ${spacing[3]};
`;

export const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  padding: ${spacing[3]};
`;

export const NavigationList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const NavigationItem = styled.li`
  display: flex;
`;

const interactiveItemStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[10]};
  height: ${spacing[10]};
  flex: 0 0 ${spacing[10]};
  box-sizing: border-box;
  border-radius: ${borderRadius.lg};
`;

export const NavigationLink = styled(Link)<{ $isActive: boolean; $wide?: boolean }>`
  ${interactiveItemStyles}
  ${({ $wide }) => $wide && css`width: 100%; flex: 1 1 auto; justify-content: flex-start; gap: ${spacing[2]}; padding: 0 ${spacing[2.5] ?? spacing[2]};`}
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.text.interactive : theme.colors.text.secondary};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary[100] : "transparent"};
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text.interactive};
    background: ${({ theme }) => theme.colors.primary[50]};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: -${borderWidth[2]};
  }
`;

export const DisabledNavigationItem = styled.span<{ $wide?: boolean }>`
  ${interactiveItemStyles}
  ${({ $wide }) => $wide && css`width: 100%; flex: 1 1 auto; justify-content: flex-start; gap: ${spacing[2]}; padding: 0 ${spacing[2.5] ?? spacing[2]};`}
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: not-allowed;
`;

export const IconFrame = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${spacing[5]};
  height: ${spacing[5]};

  svg {
    display: block;
    width: ${spacing[4]};
    height: ${spacing[4]};
  }
`;

export const CreateButton = styled.button`
  ${interactiveItemStyles}
  padding: 0;
  color: ${({ theme }) => theme.colors.text.inverse};
  background: ${({ theme }) => theme.colors.background.inverse};
  border: 0;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.background.inverseHover};
  }

  &:focus-visible {
    outline: ${borderWidth[2]} ${borderStyle.solid}
      ${({ theme }) => theme.colors.primary[500]};
    outline-offset: ${borderWidth[2]};
  }

  svg {
    display: block;
    width: ${spacing[4]};
    height: ${spacing[4]};
  }
`;
